import React, { ComponentProps } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  CloudinaryImage,
  ContentfulImage,
  Icon,
  Image,
  LoadingDots,
  PRODUCT_CARD_IMAGE_CONFIG,
  theme,
  useResponsive,
} from '@sg/garnish';

import type { HomeProductCardSize } from '../HomeProductCard/HomeProductCard.types';

// ────────────────────────────────────────────────────────────────────────────

export const HomeProductImage = (props: HomeProductImageProps) => {
  const {
    size,
    isModifiable,
    isAddingToBag,
    cost,
    cloudinaryImage,
    contentfulImage,
    fallbackImage,
    imageA11y,
  } = props;

  const { match } = useResponsive();

  const imageContainerStyleXS = size === 'regular' ? styles.imageContainerRegular : styles.imageContainerLarge; // prettier-ignore
  const imageXS = size === 'regular' ? styles.imageXSRegular : styles.imageXSLarge;

  const imageContainerStyle = match([imageContainerStyleXS, styles.imageContainerRegular]);
  const imageStyle = match([imageXS, styles.imageSM, styles.imageLG, styles.imageXL]);

  const contentfulImageConfigXS = size === 'regular' ? CONTENTFUL_IMAGE_CONFIG_XS_REGULAR : CONTENTFUL_IMAGE_CONFIG_XS_LARGE; // prettier-ignore

  const contentfulImageConfig = match([contentfulImageConfigXS, CONTENTFUL_IMAGE_CONFIG_SM, CONTENTFUL_IMAGE_CONFIG_LG, CONTENTFUL_IMAGE_CONFIG_XL]); // prettier-ignore

  return (
    <View style={imageContainerStyle}>
      {cloudinaryImage ? (
        <CloudinaryImage
          contentFit="contain"
          baseUrl={cloudinaryImage}
          config={PRODUCT_CARD_IMAGE_CONFIG}
          aria-label={imageA11y}
          style={imageStyle}
        />
      ) : null}

      {contentfulImage && !cloudinaryImage ? (
        <ContentfulImage
          contentFit="contain"
          baseUrl={contentfulImage}
          options={contentfulImageConfig}
          aria-label={imageA11y}
          style={imageStyle}
        />
      ) : null}

      {!cloudinaryImage && !contentfulImage && fallbackImage ? (
        <Image
          contentFit="contain"
          source={fallbackImage}
          aria-label={imageA11y}
          style={imageStyle}
        />
      ) : null}

      {isModifiable || !cost ? null : (
        <View style={styles.addToBagIcon} pointerEvents="none">
          {isAddingToBag ? (
            <LoadingDots color={theme.colors.DARK_KALE} />
          ) : (
            <Icon
              name="IconPlus"
              width={ADD_ICON_SIZE}
              height={ADD_ICON_SIZE}
              color={theme.colors.DARK_KALE}
            />
          )}
        </View>
      )}
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

export const HOME_PRODUCT_IMAGE_SIZE_XS_REGULAR = 160;
export const HOME_PRODUCT_IMAGE_SIZE_XS_LARGE = 236;
export const HOME_PRODUCT_IMAGE_SIZE_SM = 270;
export const HOME_PRODUCT_IMAGE_SIZE_LG = 270;
export const HOME_PRODUCT_IMAGE_SIZE_XL = 365;

export const HOME_PRODUCT_CARD_SIZES_REGULAR = [
  HOME_PRODUCT_IMAGE_SIZE_XS_REGULAR,
  HOME_PRODUCT_IMAGE_SIZE_SM,
  HOME_PRODUCT_IMAGE_SIZE_LG,
  HOME_PRODUCT_IMAGE_SIZE_XL,
];

export const HOME_PRODUCT_CARD_SIZES_LARGE = [
  HOME_PRODUCT_IMAGE_SIZE_XS_LARGE,
  HOME_PRODUCT_IMAGE_SIZE_SM,
  HOME_PRODUCT_IMAGE_SIZE_LG,
  HOME_PRODUCT_IMAGE_SIZE_XL,
];

const ADD_ICON_SIZE = 24;
const ADD_BUTTON_SIZE = 32;

const SHARED_CONTENTFUL_OPTIONS = {
  fm: 'webp' as const,
  fit: 'fill' as const,
  q: 70,
};

const CONTENTFUL_IMAGE_CONFIG_XS_REGULAR = {
  ...SHARED_CONTENTFUL_OPTIONS,
  w: HOME_PRODUCT_IMAGE_SIZE_XS_REGULAR,
};
const CONTENTFUL_IMAGE_CONFIG_XS_LARGE = {
  ...SHARED_CONTENTFUL_OPTIONS,
  w: HOME_PRODUCT_IMAGE_SIZE_XS_LARGE,
};
const CONTENTFUL_IMAGE_CONFIG_SM = { ...SHARED_CONTENTFUL_OPTIONS, w: HOME_PRODUCT_IMAGE_SIZE_SM };
const CONTENTFUL_IMAGE_CONFIG_LG = { ...SHARED_CONTENTFUL_OPTIONS, w: HOME_PRODUCT_IMAGE_SIZE_LG };
const CONTENTFUL_IMAGE_CONFIG_XL = { ...SHARED_CONTENTFUL_OPTIONS, w: HOME_PRODUCT_IMAGE_SIZE_XL };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  imageContainerRegular: {
    alignItems: 'center',
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.medium,
    overflow: 'hidden',
  },
  imageContainerLarge: {
    alignItems: 'center',
  },
  imageXSRegular: {
    width: HOME_PRODUCT_IMAGE_SIZE_XS_REGULAR,
    height: HOME_PRODUCT_IMAGE_SIZE_XS_REGULAR,
  },
  imageXSLarge: {
    width: HOME_PRODUCT_IMAGE_SIZE_XS_LARGE,
    height: HOME_PRODUCT_IMAGE_SIZE_XS_LARGE,
  },
  imageSM: {
    width: HOME_PRODUCT_IMAGE_SIZE_SM,
    height: HOME_PRODUCT_IMAGE_SIZE_SM,
  },
  imageLG: {
    width: HOME_PRODUCT_IMAGE_SIZE_LG,
    height: HOME_PRODUCT_IMAGE_SIZE_LG,
  },
  imageXL: {
    width: HOME_PRODUCT_IMAGE_SIZE_XL,
    height: HOME_PRODUCT_IMAGE_SIZE_XL,
  },
  addToBagIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: ADD_BUTTON_SIZE,
    height: ADD_BUTTON_SIZE,
    borderRadius: ADD_BUTTON_SIZE,
    backgroundColor: theme.colors.WHITE,
    position: 'absolute',
    bottom: theme.spacing['2'],
    right: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeProductImageProps = {
  size: HomeProductCardSize;
  isModifiable: boolean;
  isAddingToBag: boolean;
  cost?: number;
  cloudinaryImage?: string;
  contentfulImage?: string;
  fallbackImage?: ComponentProps<typeof Image>['source'];
  imageA11y: string;
};
